import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useNavigate } from "react-router";
import { LogOut, ShieldCheck, Trash2 } from "lucide-react";
import { paths } from "../../app/paths.js";
import type { OrgDetail, OrgMemberView } from "../../api/teams.js";
import { useDeleteOrg, useLeaveTeam, useOwnMembership, useRotateTeamKey, useUpdateOrg } from "../../data/teams.js";
import { canManageTeam, describeTeamError, isOwnerRole } from "../../lib/teams.js";
import { useToast } from "../../ui/Toast.js";
import { Field, Switch, TextInput } from "../../ui/forms.js";
import { Alert, Button, ConfirmInline, Section } from "../../ui/primitives.js";

interface SettingsTabProps {
  readonly org: OrgDetail;
  readonly members: ReadonlyArray<OrgMemberView> | undefined;
}

export function TeamSettingsTab(props: SettingsTabProps): ReactNode {
  const { org } = props;
  const manage = canManageTeam(org.role);
  return (
    <div className="rounded-xl border border-line bg-ink-2 px-5 sm:px-7">
      {manage ? <GeneralSection org={org} /> : null}
      {manage ? <KeySection org={org} /> : null}
      <MembershipSection org={org} members={props.members} />
    </div>
  );
}

function GeneralSection(props: { readonly org: OrgDetail }): ReactNode {
  const { org } = props;
  const update = useUpdateOrg(org.id);
  const toast = useToast();
  const [name, setName] = useState(org.name);
  const [error, setError] = useState<string | undefined>(undefined);

  function save(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === "" || trimmed.length > 128) {
      setError("Bitte gib einen Namen mit höchstens 128 Zeichen ein.");
      return;
    }
    update.mutate(
      { name: trimmed },
      {
        onSuccess: (): void => toast.notify("Name gespeichert.", { tone: "success" }),
        onError: (err: Error): void => setError(describeTeamError(err))
      }
    );
  }

  return (
    <Section title="Allgemein" description="Name und Anmeldepflicht für das Team.">
      <div className="flex flex-col gap-5">
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end" noValidate>
          <Field id="team-settings-name" label="Name" error={error}>
            <TextInput
              id="team-settings-name"
              value={name}
              maxLength={128}
              autoComplete="off"
              onChange={(event): void => {
                setName(event.target.value);
                setError(undefined);
              }}
            />
          </Field>
          <Button type="submit" variant="secondary" loading={update.isPending} disabled={name.trim() === org.name}>
            Speichern
          </Button>
        </form>
        <div className="rounded-lg border border-line bg-ink px-4 py-3">
          <Switch
            label="Zwei-Faktor-Anmeldung erzwingen"
            description="Mitglieder ohne Authenticator oder Passkey verlieren den Zugriff, bis sie 2FA eingerichtet haben."
            checked={org.require2fa}
            disabled={update.isPending}
            onChange={(checked: boolean): void => {
              update.mutate(
                { require2fa: checked },
                {
                  onSuccess: (): void => toast.notify(checked ? "2FA ist jetzt Pflicht." : "2FA ist nicht mehr Pflicht.", { tone: "success" }),
                  onError: (err: Error): void => toast.notify(describeTeamError(err), { tone: "danger" })
                }
              );
            }}
          />
        </div>
      </div>
    </Section>
  );
}

function KeySection(props: { readonly org: OrgDetail }): ReactNode {
  const { org } = props;
  const rotate = useRotateTeamKey(org.id);
  const toast = useToast();

  return (
    <Section
      title="Team-Schlüssel"
      description="Schützt alle gemeinsamen Einträge. Nach dem Entfernen eines Mitglieds wird er automatisch rotiert."
    >
      <div className="flex flex-col gap-4">
        {org.rotationPending ? (
          <Alert tone="danger">Die Rotation nach einer Entfernung ist noch offen. Bis dahin könnte das entfernte Mitglied alte Ciphertexte besitzen.</Alert>
        ) : (
          <Alert tone="success">Der Schlüssel ist aktuell. Version {org.keyVersion}.</Alert>
        )}
        <div>
          <Button
            variant="secondary"
            icon={ShieldCheck}
            loading={rotate.isPending}
            onClick={(): void => {
              rotate.mutate(undefined, {
                onSuccess: (version: number): void => toast.notify(`Schlüssel rotiert (Version ${version}).`, { tone: "success" }),
                onError: (err: Error): void => toast.notify(describeTeamError(err), { tone: "danger" })
              });
            }}
          >
            Schlüssel jetzt rotieren
          </Button>
        </div>
      </div>
    </Section>
  );
}

function MembershipSection(props: { readonly org: OrgDetail; readonly members: ReadonlyArray<OrgMemberView> | undefined }): ReactNode {
  const { org } = props;
  const navigate = useNavigate();
  const toast = useToast();
  const self = useOwnMembership(props.members);
  const leave = useLeaveTeam(org.id);
  const remove = useDeleteOrg(org.id);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <Section title="Mitgliedschaft" description="Team verlassen oder endgültig löschen.">
      <div className="flex flex-col gap-3">
        {self !== undefined ? (
          confirmLeave ? (
            <ConfirmInline
              message="Team verlassen? Du verlierst den Zugriff auf alle gemeinsamen Einträge."
              confirmLabel="Verlassen"
              pending={leave.isPending}
              onCancel={(): void => setConfirmLeave(false)}
              onConfirm={(): void => {
                leave.mutate(self.userId, {
                  onSuccess: (): void => {
                    toast.notify("Du hast das Team verlassen.", { tone: "success" });
                    void navigate(paths.teams);
                  },
                  onError: (err: Error): void => {
                    toast.notify(describeTeamError(err), { tone: "danger" });
                    setConfirmLeave(false);
                  }
                });
              }}
            />
          ) : (
            <div className="flex flex-col gap-3 rounded-lg border border-line bg-ink px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-mist">Du verlierst sofort den Zugriff auf die Einträge dieses Teams.</p>
              <Button variant="secondary" size="sm" icon={LogOut} onClick={(): void => setConfirmLeave(true)}>
                Team verlassen
              </Button>
            </div>
          )
        ) : null}

        {isOwnerRole(org.role) ? (
          confirmDelete ? (
            <ConfirmInline
              message={`Team „${org.name}“ endgültig löschen? Alle Sammlungen und Einträge werden entfernt.`}
              confirmLabel="Endgültig löschen"
              pending={remove.isPending}
              onCancel={(): void => setConfirmDelete(false)}
              onConfirm={(): void => {
                remove.mutate(undefined, {
                  onSuccess: (): void => {
                    toast.notify("Team gelöscht.", { tone: "success" });
                    void navigate(paths.teams);
                  },
                  onError: (err: Error): void => {
                    toast.notify(describeTeamError(err), { tone: "danger" });
                    setConfirmDelete(false);
                  }
                });
              }}
            />
          ) : (
            <div className="flex flex-col gap-3 rounded-lg border border-buoy/30 bg-buoy/[0.04] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-mist">Löscht das Team mit allen Sammlungen und Einträgen. Nicht umkehrbar.</p>
              <Button variant="danger" size="sm" icon={Trash2} onClick={(): void => setConfirmDelete(true)}>
                Team löschen
              </Button>
            </div>
          )
        ) : null}
      </div>
    </Section>
  );
}
